import * as React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Link as LinkIcon,
  Undo,
  Redo,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { usePrompt } from "../confirm-provider";
import type { EditorHandle } from "./html-code-editor";

export interface VisualEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

function ToolbarButton({
  onClick,
  isActive,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:pointer-events-none disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100",
        isActive &&
          "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100",
      )}
    >
      {children}
    </button>
  );
}

export const VisualEditor = React.forwardRef<EditorHandle, VisualEditorProps>(
  ({ value, onChange, placeholder, className }, ref) => {
    const prompt = usePrompt();
    const editor = useEditor({
      extensions: [
        StarterKit,
        Underline,
        Link.configure({ openOnClick: false }),
        Placeholder.configure({ placeholder: placeholder ?? "Start writing…" }),
      ],
      content: value,
      onUpdate: ({ editor }) => {
        onChange(editor.getHTML());
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-sm dark:prose-invert max-w-none h-full min-h-full focus:outline-none focus-fill px-4 py-3 text-slate-900 dark:text-slate-100",
        },
      },
    });

    React.useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      if (value !== current) {
        editor.commands.setContent(value, false);
      }
    }, [editor, value]);

    React.useImperativeHandle(ref, () => ({
      insertAtCursor: (text: string) => {
        editor?.chain().focus().insertContent(text).run();
      },
      focus: () => editor?.commands.focus(),
    }));

    const setLink = async () => {
      if (!editor) return;
      const prev = editor.getAttributes("link").href as string | undefined;
      const url = await prompt({
        title: prev ? "Update link" : "Add link",
        label: "URL",
        placeholder: "https://",
        defaultValue: prev ?? "https://",
        confirmText: "Save link",
      });
      if (url === null) return;
      if (url === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    };

    if (!editor) {
      return (
        <div
          className={cn(
            "flex h-full w-full items-center justify-center rounded-lg bg-sunken text-sm text-text-tertiary",
            className,
          )}
        >
          Loading editor…
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex h-full w-full flex-col overflow-hidden rounded-lg bg-sunken",
          className,
        )}
      >
        <div className="flex flex-wrap items-center gap-1 bg-sunken px-2 py-1.5">
          <ToolbarButton
            title="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive("bold")}
          >
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive("italic")}
          >
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive("underline")}
          >
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Strikethrough"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive("strike")}
          >
            <Strikethrough className="h-4 w-4" />
          </ToolbarButton>
          {/* Toolbar groups separate by space; the spacer holds the rhythm the
              old 1px rule occupied. */}
          <span className="mx-1 h-5 w-px" />
          <ToolbarButton
            title="Heading 1"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            isActive={editor.isActive("heading", { level: 1 })}
          >
            <Heading1 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Heading 2"
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            isActive={editor.isActive("heading", { level: 2 })}
          >
            <Heading2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Bullet list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive("bulletList")}
          >
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Ordered list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive("orderedList")}
          >
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Link"
            onClick={setLink}
            isActive={editor.isActive("link")}
          >
            <LinkIcon className="h-4 w-4" />
          </ToolbarButton>
          <span className="mx-1 h-5 w-px" />
          <ToolbarButton
            title="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            title="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="h-4 w-4" />
          </ToolbarButton>
        </div>
        <div className="flex-1 overflow-y-auto">
          <EditorContent editor={editor} className="h-full" />
        </div>
      </div>
    );
  },
);

VisualEditor.displayName = "VisualEditor";
